import { useMemo, useState } from 'react';
import { paletteFor, categoryRank } from './lib/categoryPalette.js';

// Step 3 of 4 — Assign Images.
//
// Surfaces every pipeline image_candidate from today's brief as a draggable
// pool on the right. Articles are listed on the left as drop targets. Each
// drop hits useEditionDraft.updateArticleField (debounced PUT) so the change
// is persisted and reflected throughout the website.
//
// Semantics: MOVE — assigning an image clears any other article that holds
// the same URL, so each pipeline image lives in at most one slot at a time.
// Sentinel id used by the hero (edition-level) drop slot. Pickled into the
// same ownerByUrl map as article ids so the move-semantics logic doesn't need
// a separate code path.
const HERO_ID = '__hero__';

// Returns a stable identity for an image_candidate so the pool collapses
// duplicates that share a Unsplash photo or an OG image regardless of which
// query-string variant came back from the API. Prefer the upstream photo id
// when present; otherwise normalize the URL by dropping query/hash and
// lowercasing the host.
function imageFingerprint(candidate) {
  if (!candidate?.url) return '';
  const src = (candidate.source || '').toLowerCase();
  if (candidate.photo_id) return `${src || 'photo'}|${candidate.photo_id}`;
  try {
    const u = new URL(candidate.url);
    return `${u.protocol}//${u.host.toLowerCase()}${u.pathname}`;
  } catch {
    return candidate.url;
  }
}

export default function ImageAssignment({
  edition,
  onAssign,
  onClear,
  onAssignHero,
  onClearHero,
  onBack,
  onNext,
}) {
  // Sort articles the same way the main editor does so the list mirrors the
  // brief order subscribers see.
  const sortedArticles = useMemo(() => {
    if (!edition?.articles) return [];
    return [...edition.articles].sort((a, b) => {
      const catDelta = categoryRank(a.category) - categoryRank(b.category);
      if (catDelta !== 0) return catDelta;
      return (a.order || 0) - (b.order || 0);
    });
  }, [edition]);

  // Walk pipelineMeta.articles[*].image_candidates[] and dedupe across the
  // brief. Candidates flow from the pipeline's image stage: source='og_image'
  // is scraped from the article's own OpenGraph metadata; source='unsplash'
  // is queried via the Unsplash API. Each candidate carries a url, alt_text,
  // attribution_html, and (for Unsplash) a photo_id.
  //
  // Naive URL-equality dedupe under-collapses: Unsplash serves the same photo
  // at many URLs (different ?q=, ?w=, ?ixlib= params) and the OG scraper
  // sometimes returns the same image with cache-busting query strings. We key
  // each candidate as either source|photo_id (when the API gave us a stable
  // id) or a URL stripped of its query/hash, lower-cased. That collapses size
  // variants and cache busters but still keeps distinct images apart.
  const pool = useMemo(() => {
    const meta = edition?.pipelineMeta;
    if (!meta?.articles) return [];
    const byFingerprint = new Map();
    for (const pmArticle of meta.articles) {
      const candidates = Array.isArray(pmArticle?.image_candidates)
        ? pmArticle.image_candidates
        : [];
      for (const c of candidates) {
        if (!c?.url) continue;
        const fingerprint = imageFingerprint(c);
        if (!fingerprint || byFingerprint.has(fingerprint)) continue;
        byFingerprint.set(fingerprint, {
          url: c.url,
          alt: c.alt_text || '',
          attribution: c.attribution_html || '',
          source: c.source || '',
          query: c.query || '',
          dimensions: c.dimensions || null,
          // Which pipeline article emitted this candidate — useful tooltip.
          originHeadline:
            pmArticle?.draft?.hxd_headline ||
            pmArticle?.source?.original_title ||
            '',
        });
      }
    }
    return Array.from(byFingerprint.values());
  }, [edition]);

  const heroImage = edition?.heroImage || null;

  // Reverse lookup — for any URL in the pool, what currently holds it
  // (article or hero)? Drives the "in use by" badge and the move-clear logic.
  // Each owner is { id, headline } so the badge can label hero vs article.
  const ownerByUrl = useMemo(() => {
    const map = new Map();
    for (const a of sortedArticles) {
      if (a.imageUrl) {
        map.set(a.imageUrl, {
          id: String(a._id),
          headline: a.headline || 'article',
          isHero: false,
        });
      }
    }
    if (heroImage?.url && !map.has(heroImage.url)) {
      map.set(heroImage.url, {
        id: HERO_ID,
        headline: 'Main image',
        isHero: true,
      });
    }
    return map;
  }, [sortedArticles, heroImage]);

  const [dragImage, setDragImage] = useState(null);
  const [hoverArticleId, setHoverArticleId] = useState(null);

  function handleDragStart(e, image) {
    setDragImage(image);
    // Stash the URL on dataTransfer so the drag also works across edge cases
    // where the React state hasn't propagated yet (e.g. native drag-drop
    // ghosting from the OS side).
    e.dataTransfer.setData('text/plain', image.url);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnd() {
    setDragImage(null);
    setHoverArticleId(null);
  }

  function handleDragOver(e, articleId) {
    if (!dragImage) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverArticleId !== articleId) setHoverArticleId(articleId);
  }

  function handleDrop(e, articleId) {
    e.preventDefault();
    const image = dragImage;
    setDragImage(null);
    setHoverArticleId(null);
    if (!image) return;
    // No-op if dropping the image on its current owner.
    const current = ownerByUrl.get(image.url);
    if (current && current.id === articleId) return;
    if (articleId === HERO_ID) {
      onAssignHero?.(image);
    } else {
      onAssign?.(articleId, image);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
            Step 3 of 4 · Assign Images
          </div>
          <h1 className="hxd-page-title">Pick the image for each article</h1>
          <p className="hxd-page-subtitle">
            Drag a thumbnail onto an article. Each image can only live in one
            slot — moving it clears its previous home.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm"
        >
          ← Back to Final Check
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-2 min-w-0 pb-24">
          <HeroSlot
            heroImage={heroImage}
            hovered={hoverArticleId === HERO_ID}
            draggingImage={dragImage}
            onDragOver={(e) => handleDragOver(e, HERO_ID)}
            onDragLeave={() => setHoverArticleId(null)}
            onDrop={(e) => handleDrop(e, HERO_ID)}
            onDragStartCurrent={(e) => {
              if (!heroImage?.url) return;
              handleDragStart(e, {
                url: heroImage.url,
                alt: heroImage.alt || '',
                attribution: heroImage.attributionHtml || '',
              });
            }}
            onClear={() => onClearHero?.()}
          />
          {sortedArticles.length === 0 ? (
            <div className="hxd-card text-[12px] text-hxd-muted italic">
              No articles in this brief yet.
            </div>
          ) : (
            sortedArticles.map((article, idx) => (
              <ArticleSlot
                key={String(article._id)}
                index={idx}
                article={article}
                hovered={hoverArticleId === String(article._id)}
                draggingImage={dragImage}
                onDragOver={(e) => handleDragOver(e, String(article._id))}
                onDragLeave={() => setHoverArticleId(null)}
                onDrop={(e) => handleDrop(e, String(article._id))}
                onDragStartCurrent={(e) => {
                  if (!article.imageUrl) return;
                  handleDragStart(e, {
                    url: article.imageUrl,
                    alt: article.imageAlt || '',
                    attribution: article.imageAttributionHtml || '',
                  });
                }}
                onClear={() => onClear?.(String(article._id))}
              />
            ))
          )}
        </div>

        <aside className="space-y-3 md:sticky md:top-4 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto md:pr-1">
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted">
            Image pool · {pool.length}
          </div>
          {pool.length === 0 ? (
            <div className="hxd-card text-[12px] text-hxd-muted italic">
              No pipeline image candidates available for this edition.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {pool.map((image) => {
                const owner = ownerByUrl.get(image.url);
                return (
                  <PoolThumb
                    key={image.url}
                    image={image}
                    owner={owner}
                    onDragStart={(e) => handleDragStart(e, image)}
                    onDragEnd={handleDragEnd}
                  />
                );
              })}
            </div>
          )}
        </aside>
      </div>

      <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 mt-2 px-4 sm:px-6 py-3 bg-black/95 backdrop-blur border-t border-[#333] flex items-center gap-3">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted">
          Step 3 of 4
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={onNext}
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
          >
            Preview email →
          </button>
        </div>
      </div>
    </div>
  );
}

function ArticleSlot({
  index,
  article,
  hovered,
  draggingImage,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStartCurrent,
  onClear,
}) {
  const palette = paletteFor(article.category);
  const hasImage = !!article.imageUrl;

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={
        'flex items-stretch gap-3 p-2 rounded-[3px] border bg-white transition-colors ' +
        (hovered
          ? 'border-hxd-orange ring-2 ring-hxd-orange/40'
          : 'border-hxd-border-soft')
      }
      style={{ borderLeftWidth: 4, borderLeftColor: palette.bar }}
    >
      <div
        className={
          'w-[140px] h-[80px] flex-shrink-0 rounded-[2px] border ' +
          (hasImage
            ? 'border-hxd-border-soft bg-white overflow-hidden cursor-grab'
            : 'border-dashed border-hxd-muted bg-hxd-bg flex items-center justify-center')
        }
        draggable={hasImage}
        onDragStart={onDragStartCurrent}
      >
        {hasImage ? (
          <img
            src={article.imageUrl}
            alt={article.imageAlt || ''}
            className="w-full h-full object-cover pointer-events-none"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="text-[10px] font-display font-bold uppercase tracking-display-sm text-hxd-muted">
            {draggingImage ? 'Drop here' : 'No image'}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="font-display text-[10px] font-bold leading-none"
              style={{ color: palette.ink }}
            >
              {String(index + 1).padStart(2, '0')}
            </span>
            <span
              className="font-display text-[10px] font-bold tracking-display-sm uppercase"
              style={{ color: palette.ink }}
            >
              {article.category || 'Uncategorized'}
            </span>
          </div>
          <div className="text-[13px] font-semibold text-hxd-ink line-clamp-2 break-words">
            {article.headline || 'Untitled article'}
          </div>
          {article.imageAlt && (
            <div className="text-[11px] text-hxd-muted truncate mt-0.5">
              alt: {article.imageAlt}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end">
          {hasImage && (
            <button
              type="button"
              onClick={onClear}
              className="text-[11px] text-hxd-muted hover:text-hxd-err"
            >
              Remove image
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PoolThumb({ image, owner, onDragStart, onDragEnd }) {
  const inUse = !!owner;
  const tooltip = [
    image.query && `Query: ${image.query}`,
    image.source && `Source: ${image.source}`,
    image.dimensions && `${image.dimensions.w}×${image.dimensions.h}`,
    inUse && `In use: ${owner.headline || 'article'}`,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title={tooltip}
      className={
        'relative rounded-[2px] border overflow-hidden cursor-grab bg-white ' +
        (inUse ? 'border-hxd-orange' : 'border-hxd-border-soft')
      }
    >
      <img
        src={image.url}
        alt={image.alt}
        className="block w-full h-[90px] object-cover pointer-events-none"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
      {inUse && (
        <span className="absolute top-1 left-1 text-[9px] font-display font-bold uppercase tracking-display-sm px-1 py-0.5 bg-hxd-orange text-white rounded-[2px]">
          {owner.isHero ? 'Main' : 'In use'}
        </span>
      )}
    </div>
  );
}

// Edition-level "main image" drop target. Renders above the article list so
// the editor reads it as the issue hero (matches the email layout where the
// hero sits above the daily summary). Uses the same drag/drop wiring as
// ArticleSlot but ignores category palette since the hero is edition-scoped.
function HeroSlot({
  heroImage,
  hovered,
  draggingImage,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStartCurrent,
  onClear,
}) {
  const hasImage = !!heroImage?.url;
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={
        'flex items-stretch gap-3 p-2 rounded-[3px] border bg-white transition-colors mb-3 ' +
        (hovered
          ? 'border-hxd-orange ring-2 ring-hxd-orange/40'
          : 'border-hxd-border-soft')
      }
      style={{ borderLeftWidth: 4, borderLeftColor: '#0A0A0A' }}
    >
      <div
        className={
          'w-[200px] h-[112px] flex-shrink-0 rounded-[2px] border ' +
          (hasImage
            ? 'border-hxd-border-soft bg-white overflow-hidden cursor-grab'
            : 'border-dashed border-hxd-muted bg-hxd-bg flex items-center justify-center')
        }
        draggable={hasImage}
        onDragStart={onDragStartCurrent}
      >
        {hasImage ? (
          <img
            src={heroImage.url}
            alt={heroImage.alt || ''}
            className="w-full h-full object-cover pointer-events-none"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="text-[10px] font-display font-bold uppercase tracking-display-sm text-hxd-muted">
            {draggingImage ? 'Drop here' : 'No main image'}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        <div className="min-w-0">
          <div className="font-display text-[10px] font-bold tracking-display-sm uppercase text-hxd-ink mb-0.5">
            Main image
          </div>
          <div className="text-[13px] font-semibold text-hxd-ink">
            Appears above the daily summary
          </div>
          {heroImage?.alt && (
            <div className="text-[11px] text-hxd-muted truncate mt-0.5">
              alt: {heroImage.alt}
            </div>
          )}
          {!hasImage && (
            <div className="text-[11px] text-hxd-muted italic mt-0.5">
              Drag an image from the pool to set the main image for this brief.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end">
          {hasImage && (
            <button
              type="button"
              onClick={onClear}
              className="text-[11px] text-hxd-muted hover:text-hxd-err"
            >
              Remove image
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
